import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FiSearch, FiX } from 'react-icons/fi';
import { productsAPI } from '../../api/products';
import { Product } from '../../types/product';
import { formatCurrency } from '../../utils/currency';
import { getLocalizedField } from '../../utils/localize';
import type { SupportedLanguage } from '../../i18n';

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  min-width: 200px;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 1rem;
  width: 20px;
  height: 20px;
  color: var(--gray-dark);
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 3rem;
  padding-right: ${props => props.value ? '3rem' : '1rem'};
  border: 1px solid var(--gray-medium);
  border-radius: 4px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }

  &::placeholder {
    color: var(--gray-dark);
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: none;
  border: none;
  color: var(--gray-dark);
  cursor: pointer;
  padding: 0;

  &:hover {
    color: var(--text-color);
  }
`;

const SuggestionsDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 0.5rem;
  background: var(--white);
  border: 1px solid var(--gray-medium);
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 400px;
  overflow-y: auto;
  z-index: 100;
  display: ${props => props.$isOpen ? 'block' : 'none'};
`;

const SuggestionItem = styled.div<{ $isHighlighted: boolean }>`
  padding: 0.75rem 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 1rem;
  background-color: ${props => props.$isHighlighted ? 'var(--gray-light)' : 'transparent'};
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--gray-light);
  }

  &:not(:last-child) {
    border-bottom: 1px solid var(--gray-light);
  }
`;

const ProductImage = styled.img`
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
`;

const ProductInfo = styled.div`
  flex: 1;
`;

const ProductName = styled.div`
  font-weight: 500;
  color: var(--text-color);
`;

const ProductCategory = styled.div`
  font-size: 0.85rem;
  color: var(--gray-dark);
`;

const ProductPrice = styled.div`
  font-weight: bold;
  color: var(--primary-color);
`;

const NoResults = styled.div`
  padding: 1.5rem;
  text-align: center;
  color: var(--gray-dark);
`;

const SearchHint = styled.div`
  padding: 0.75rem 1rem;
  background-color: var(--gray-light);
  color: var(--gray-dark);
  font-size: 0.9rem;
  border-bottom: 1px solid var(--gray-medium);
`;

interface SearchBarProps {
  placeholder?: string;
  onSearch?: (query: string) => void;
}

const SearchBar: React.FC<SearchBarProps> = ({
  placeholder,
  onSearch
}) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Product[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  // Mirrors the LanguageToggle.tsx/ProductCard.tsx pattern: i18next's language
  // can be a region-qualified tag or unset, so narrow to the two v1-supported
  // values.
  const language: SupportedLanguage = (i18n.language as SupportedLanguage) === 'zh' ? 'zh' : 'en';
  const resolvedPlaceholder = placeholder ?? t('searchBar.placeholder');

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (query.length < 2) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(async () => {
      setIsLoading(true);
      try {
        const results = await productsAPI.getProducts({
          search: query,
          limit: 8
        });
        setSuggestions(results);
        setIsOpen(results.length > 0);
      } catch (error) {
        console.error('Failed to fetch suggestions:', error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, 300); // 300ms debounce delay

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [query]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setHighlightedIndex(-1);
  };

  const handleClear = () => {
    setQuery('');
    setSuggestions([]);
    setIsOpen(false);
    inputRef.current?.focus();
    if (onSearch) {
      onSearch('');
    }
  };

  const handleSelectProduct = (product: Product) => {
    navigate(`/products/${product.product_id}`);
    setQuery('');
    setSuggestions([]);
    setIsOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/products?search=${encodeURIComponent(query)}`);
      setIsOpen(false);
      if (onSearch) {
        onSearch(query);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
        handleSelectProduct(suggestions[highlightedIndex]);
      } else {
        handleSubmit(e);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }
  };

  return (
    <SearchContainer ref={searchRef}>
      <form onSubmit={handleSubmit}>
        <SearchInputWrapper>
          <SearchIconWrapper>
            <FiSearch />
          </SearchIconWrapper>
          <SearchInput
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => query.length >= 2 && suggestions.length > 0 && setIsOpen(true)}
            placeholder={resolvedPlaceholder}
            autoComplete="off"
            data-testid="search-bar-input"
          />
          {query && (
            <ClearButton type="button" onClick={handleClear}>
              <FiX />
            </ClearButton>
          )}
        </SearchInputWrapper>
      </form>

      <SuggestionsDropdown $isOpen={isOpen}>
        {query.length >= 2 && (
          <SearchHint>
            {t('searchBar.pressEnterToSearch', { query })}
          </SearchHint>
        )}

        {isLoading ? (
          <NoResults>{t('searchBar.searching')}</NoResults>
        ) : suggestions.length > 0 ? (
          suggestions.map((product, index) => (
            <SuggestionItem
              key={product.product_id}
              $isHighlighted={index === highlightedIndex}
              onClick={() => handleSelectProduct(product)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <ProductImage
                src="https://images.unsplash.com/photo-1578985545062-69928b1d9587"
                alt={getLocalizedField(product, 'name', language) as string}
                loading="lazy"
              />
              <ProductInfo>
                <ProductName>{getLocalizedField(product, 'name', language) as string}</ProductName>
                <ProductCategory>
                  {(() => {
                    const localizedDescription = getLocalizedField(product, 'description', language) as string | undefined;
                    return localizedDescription && localizedDescription.length > 50
                      ? localizedDescription.substring(0, 50) + '...'
                      : localizedDescription;
                  })()}
                </ProductCategory>
              </ProductInfo>
              <ProductPrice>{formatCurrency(product.base_price)}</ProductPrice>
            </SuggestionItem>
          ))
        ) : query.length >= 2 && !isLoading ? (
          <NoResults>{t('searchBar.noResultsFor', { query })}</NoResults>
        ) : null}
      </SuggestionsDropdown>
    </SearchContainer>
  );
};

export default SearchBar;